import React, { useState } from 'react';
import {
  Popconfirm,
  message,
  Empty,
  Modal,
  Input,
  Typography,
  notification,
  Tooltip,
  Button,
} from 'antd';
import PaginationTable from '../../../../shared/PaginationTable';
import {
  DeleteOutlined,
  EditOutlined,
  CheckCircleTwoTone,
  FileSearchOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import Loader from '../../../../shared/Loader';
import moment from 'moment';
import { useBreakpoints } from '../../../../shared/globalStyling/screenSizes';
import { text, feedback, dateFormat, pagination } from '../../../../ux/Tasks/configs/designTokens';

const ModalNotes = ({
  isModalOpen,
  isModal2Open,
  setIsModalOpen,
  setIsModal2Open,
  addLocationNotes,
  locationSelected,
  locationNotesView,
  setLocationNoteView,
}) => {
  const { TextArea } = Input;

  return (
    <>
      <Modal
        title="Add Notes"
        visible={isModalOpen}
        wrapClassName="targeting-root"
        onOk={() => {
          addLocationNotes({
            variables: {
              id: locationSelected,
              notes: locationNotesView,
              hasNotes: true,
            },
          });
          setIsModalOpen(false);
          notification.open({
            message: 'Creative remark submitted',
            description: 'Success!',
            icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
          });
        }}
        onCancel={() => setIsModalOpen(false)}
      >
        <TextArea
          rows={4}
          placeholder="Add Notes"
          defaultValue={locationNotesView}
          value={locationNotesView}
          onChange={(val) => {
            setLocationNoteView(val.target.value);
          }}
        />
      </Modal>
      <Modal
        title="View Notes"
        visible={isModal2Open}
        wrapClassName="targeting-root"
        onOk={() => {
          setIsModal2Open(false);
        }}
        onCancel={() => setIsModal2Open(false)}
      >
        <Typography>{locationNotesView}</Typography>
      </Modal>
    </>
  );
};

// ─── Helper: clean address ──────────────────────────────────────────────
function cleanAddress(addr) {
  if (!addr) return '';
  return addr.trim().replace(/^, */, '');
}

// ─── Helper: get display radius ─────────────────────────────────────────
function getDisplayRadius(record) {
  if (record.geoframe && record.geoframe.circles && record.geoframe.circles.length) {
    return (record.geoframe.circles[0].radius * 1000).toLocaleString();
  }
  return record.radius ? record.radius.toLocaleString() : '0';
}

// ─── Card view (tablet / mobile) ────────────────────────────────────────
const LocationCardList = ({
  locations,
  selectedRowKeys,
  onSelect,
  editLocation,
  hideRowSelection,
  expandedCardId,
  onCardExpand,
}) => (
  <div className="loc-card-list">
    {locations.map((record) => {
      const isSelected = selectedRowKeys.includes(record.id);
      const isExpanded = expandedCardId === record.id;
      const address = cleanAddress(record.formattedAddress);
      const hasGeo = record.geoframe !== null;

      return (
        <div key={record.id} className={`loc-card-wrap${isSelected ? ' loc-card-wrap--selected' : ''}`}>
          <div
            className="loc-card"
            onClick={() => !hideRowSelection && hasGeo && onSelect(record.id)}
          >
            <div className="loc-card__header">
              <span className="loc-card__name">{record.name}</span>
              <button
                type="button"
                className="loc-card__edit-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  editLocation(record);
                }}
              >
                <EditOutlined />
              </button>
            </div>
            {address && (
              <div className="loc-card__address">
                {!hasGeo && <span className="loc-card__no-geo">⚠ </span>}
                {address}
              </div>
            )}
            <div className="loc-card__meta">
              <span className="loc-card__stat">
                <span className="loc-card__radius-badge">{getDisplayRadius(record)} mi</span>
              </span>
              <span className="loc-card__stat">
                <CalendarOutlined /> {moment(record.createdAt).format(dateFormat.dateOnly)}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="loc-card__expand"
            onClick={() => onCardExpand(isExpanded ? null : record.id)}
          >
            {isExpanded ? 'Less info' : 'More info'}
            <span className={`loc-card__chevron${isExpanded ? ' loc-card__chevron--open' : ''}`}>
              &#9662;
            </span>
          </button>
          {isExpanded && (
            <div className="loc-card__details">
              <div className="loc-card__detail-row">
                <span className="loc-card__detail-label">Latitude</span>
                <span className="loc-card__detail-value">{record.lat}</span>
              </div>
              <div className="loc-card__detail-row">
                <span className="loc-card__detail-label">Longitude</span>
                <span className="loc-card__detail-value">{record.lng}</span>
              </div>
              <div className="loc-card__detail-row">
                <span className="loc-card__detail-label">Created</span>
                <span className="loc-card__detail-value">
                  {moment(record.createdAt).format(dateFormat.short)}
                </span>
              </div>
              <div className="loc-card__detail-row">
                <span className="loc-card__detail-label">Updated</span>
                <span className="loc-card__detail-value">
                  {moment(record.updatedAt).format(dateFormat.short)}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

export default function TargetingLocationListView({
  deleteLocation,
  editLocation,
  onChangeStateValue,
  hideRowSelection,
  targetingLocationList,
  audienceType = 'TARGETING',
  hiddenColumns,
  isAdmin,
  addLocationNotes,
  status,
  selectedLocations = [],
  highlightedLocationId,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModal2Open, setIsModal2Open] = useState(false);
  const [locationSelected, setLocationSelected] = useState('');
  const [locationNotesView, setLocationNoteView] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(pagination.defaultPageSize);
  const [expandedCardId, setExpandedCardId] = useState(null);

  const { isTablet } = useBreakpoints();

  const handleDeleteLocation = async (record) => {
    setLoading(true);
    if (record.audienceType === 'ADDRESS') {
      await deleteLocation(record);
    } else {
      await deleteLocation(record.id);
    }
    setLoading(false);
  };

  // ─── Compact column set ─────────────────────────────────────────────
  let columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <div className="loc-row__name">
          <div className="loc-row__name-text">
            <Tooltip title={name}>{name}</Tooltip>
          </div>
          <div className="loc-row__name-sub">
            {moment(record.createdAt).format(dateFormat.dateOnly)}
          </div>
        </div>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'formattedAddress',
      key: 'formattedAddress',
      ellipsis: true,
      render: (formattedAddress, record) => {
        const cleaned = cleanAddress(formattedAddress);
        if (!cleaned) return null;

        return (
          <Tooltip title={cleaned}>
            <span className="loc-row__address-cell">
              {record.geoframe === null && <span className="loc-row__no-geo">⚠ </span>}
              {cleaned}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Radius',
      dataIndex: 'radius',
      key: 'radius',
      width: 100,
      align: 'right',
      render: (radius, record) => {
        let displayRadius;
        if (record.geoframe && record.geoframe.circles && record.geoframe.circles.length) {
          displayRadius = (record.geoframe.circles[0].radius * 1000).toLocaleString();
        } else {
          displayRadius = radius ? radius.toLocaleString() : '0';
        }
        return <span className="loc-row__radius-badge">{displayRadius} mi</span>;
      },
    },
    {
      title: '',
      dataIndex: 'option',
      key: 'option',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <div className="loc-row__actions">
          <Tooltip title="Edit">
            <button
              className="loc-row__action-btn"
              onClick={(e) => {
                e.stopPropagation();
                editLocation(record);
              }}
            >
              <EditOutlined />
            </button>
          </Tooltip>

          {record.hasNotes === true && isAdmin === false && (
            <Tooltip title="View Remarks">
              <button
                className="loc-row__action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setLocationSelected(record.locationId);
                  setLocationNoteView(record.notes);
                  setIsModal2Open(true);
                }}
              >
                <FileSearchOutlined />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  // Reject Column (admin PENDING flow)
  if (status === 'PENDING') {
    columns.splice(columns.length - 1, 0, {
      title: 'Reject',
      dataIndex: 'reject',
      key: 'reject',
      render: (_, record) => {
        if (record.hasNotes !== true && isAdmin === true) {
          return (
            <Button
              type="primary"
              onClick={() => {
                setLocationSelected(record.mediaId);
                setLocationNoteView(record.mediaNotes);
                setIsModalOpen(true);
              }}
            >
              Reject with Note
            </Button>
          );
        }
        return null;
      },
    });
  }

  if (hiddenColumns) {
    columns = columns.filter((col) => !hiddenColumns.includes(col.key));
  }

  const filteredLocations = targetingLocationList
    ? targetingLocationList.filter((location) => location.audienceType === audienceType)
    : [];

  const selectedRowKeys = selectedLocations
    ? selectedLocations.map((location) => location.id)
    : [];

  // ─── Expandable row (lat, lng, dates) ─────────────────────────────
  const expandedRowRender = (record) => (
    <div className="loc-row__detail">
      <div className="loc-row__detail-item">
        <span className="loc-row__detail-label">Latitude</span>
        <span className="loc-row__detail-value">{record.lat}</span>
      </div>
      <div className="loc-row__detail-item">
        <span className="loc-row__detail-label">Longitude</span>
        <span className="loc-row__detail-value">{record.lng}</span>
      </div>
      <div className="loc-row__detail-item">
        <span className="loc-row__detail-label">Created</span>
        <span className="loc-row__detail-value">
          {moment(record.createdAt).format(dateFormat.short)}
        </span>
      </div>
      <div className="loc-row__detail-item">
        <span className="loc-row__detail-label">Updated</span>
        <span className="loc-row__detail-value">
          {moment(record.updatedAt).format(dateFormat.short)}
        </span>
      </div>
    </div>
  );

  const handleCardSelect = (locationId) => {
    const location = filteredLocations.find((loc) => loc.id === locationId);
    if (!location) return;
    const alreadySelected = selectedRowKeys.includes(locationId);
    const newSelection = alreadySelected
      ? selectedLocations.filter((loc) => loc.id !== locationId)
      : [...selectedLocations, location];
    onChangeStateValue('selectedLocations', newSelection);
  };

  if (!targetingLocationList || !targetingLocationList.length) {
    return <Empty />;
  }

  return (
    <div className="loc-list">
      {loading && <Loader />}
      <ModalNotes
        isModalOpen={isModalOpen}
        isModal2Open={isModal2Open}
        setIsModalOpen={setIsModalOpen}
        setIsModal2Open={setIsModal2Open}
        setLocationNoteView={setLocationNoteView}
        addLocationNotes={addLocationNotes}
        locationSelected={locationSelected}
        locationNotesView={locationNotesView}
      />

      {isTablet ? (
        <LocationCardList
          locations={filteredLocations}
          selectedRowKeys={selectedRowKeys}
          onSelect={handleCardSelect}
          editLocation={editLocation}
          hideRowSelection={hideRowSelection}
          expandedCardId={expandedCardId}
          onCardExpand={setExpandedCardId}
        />
      ) : (
        <PaginationTable
          columns={columns}
          data={filteredLocations}
          bordered={false}
          rowSelection={
            !hideRowSelection
              ? {
                  type: 'checkbox',
                  selectedRowKeys: selectedRowKeys,
                  getCheckboxProps: (record) => ({
                    disabled: record.geoframe === null,
                  }),
                  onChange: (newSelectedRowKeys, selectedRows) => {
                    onChangeStateValue('selectedLocations', selectedRows);
                  },
                }
              : null
          }
          paginationSize={'default'}
          scroll={{ x: 600 }}
          size={'small'}
          total={filteredLocations && filteredLocations.length}
          current={currentPage}
          pageSize={pageSize}
          onChange={(paginationObject, filtersObject, sorterObject) => {
            setPageSize(paginationObject.pageSize);
            setCurrentPage(paginationObject.current);
          }}
          rowKey="id"
          expandedRowRender={expandedRowRender}
          rowClassName={(record) =>
            highlightedLocationId && record.id === highlightedLocationId
              ? 'loc-row--highlighted'
              : ''
          }
        />
      )}
    </div>
  );
}
